import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAward,
  FiPlus,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiUsers,
  FiTrendingUp,
  FiX,
  FiCheck,
} from "react-icons/fi";
import "./EndorsementsTab.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Category colors
const categoryColors = {
  technical: "var(--prf-blue)",
  research: "var(--prf-purple)",
  leadership: "var(--prf-amber)",
  communication: "var(--prf-green)",
  creative: "var(--prf-pink)",
  default: "var(--prf-slate-500)",
};

// Endorsement Level Labels - matching backend API
const endorsementLevelLabels = {
  beginner: { label: "Beginner", color: "var(--prf-slate-500)" },
  intermediate: { label: "Intermediate", color: "var(--prf-blue)" },
  advanced: { label: "Advanced", color: "var(--prf-purple)" },
  expert: { label: "Expert", color: "var(--prf-amber)" },
};

// Skill Card Component - Compatible with backend API response format
// Backend returns: skill, endorsementCount, averageLevel, levelBreakdown, endorsements array
const SkillCard = ({ skill, isOwnProfile, onEndorse }) => {
  const [expanded, setExpanded] = useState(false);
  const endorsers = skill.endorsements || skill.endorsers || [];
  const endorsementCount = endorsers.length || skill.endorsementCount || 0;
  const color =
    categoryColors[skill.category?.toLowerCase()] || categoryColors.default;

  // Get average level from backend or calculate
  const averageLevel = skill.averageLevel || 2; // Default to intermediate
  const levelBreakdown = skill.levelBreakdown || {};

  return (
    <motion.div className="edt-skill-card" variants={itemVariants} layout>
      <div className="edt-skill-header">
        <div className="edt-skill-main">
          <div
            className="edt-skill-icon"
            style={{ backgroundColor: `${color}15`, color }}
          >
            <FiAward />
          </div>
          <div className="edt-skill-info">
            <h3 className="edt-skill-name">{skill.skill || skill.name}</h3>
            {skill.category && (
              <span className="edt-skill-category" style={{ color }}>
                {skill.category}
              </span>
            )}
            {/* Average Level Badge */}
            {skill.averageLevel && (
              <span
                className="edt-skill-level"
                style={{
                  color:
                    endorsementLevelLabels[
                      averageLevel >= 3.5
                        ? "expert"
                        : averageLevel >= 2.5
                        ? "advanced"
                        : averageLevel >= 1.5
                        ? "intermediate"
                        : "beginner"
                    ]?.color,
                }}
              >
                {averageLevel >= 3.5
                  ? "Expert"
                  : averageLevel >= 2.5
                  ? "Advanced"
                  : averageLevel >= 1.5
                  ? "Intermediate"
                  : "Beginner"}{" "}
                Level
              </span>
            )}
          </div>
        </div>
        <div className="edt-skill-actions">
          <div className="edt-endorsement-count">
            <FiUsers />
            <span>{endorsementCount}</span>
          </div>
          {!isOwnProfile && (
            <motion.button
              className="edt-endorse-btn"
              onClick={() => onEndorse?.(skill)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiPlus />
              <span>Endorse</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="edt-skill-progress">
        <div
          className="edt-progress-bar"
          style={{
            width: `${Math.min((endorsementCount / 20) * 100, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>

      {/* Level Breakdown (from backend) */}
      {Object.keys(levelBreakdown).length > 0 && (
        <div className="edt-level-breakdown">
          {levelBreakdown.expert > 0 && (
            <span className="edt-level-badge expert">
              {levelBreakdown.expert} Expert
            </span>
          )}
          {levelBreakdown.advanced > 0 && (
            <span className="edt-level-badge advanced">
              {levelBreakdown.advanced} Advanced
            </span>
          )}
          {levelBreakdown.intermediate > 0 && (
            <span className="edt-level-badge intermediate">
              {levelBreakdown.intermediate} Intermediate
            </span>
          )}
          {levelBreakdown.beginner > 0 && (
            <span className="edt-level-badge beginner">
              {levelBreakdown.beginner} Beginner
            </span>
          )}
        </div>
      )}

      {/* Endorsers Preview */}
      {endorsers.length > 0 && (
        <div className="edt-endorsers">
          <div className="edt-endorsers-avatars">
            {endorsers.slice(0, 5).map((endorser, i) => {
              // Backend format: endorser object has endorser.endorser for the user info
              const endorserInfo = endorser.endorser || endorser;
              return (
                <img
                  key={i}
                  src={
                    endorserInfo.profilePhotoUrl ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      endorserInfo.name || "User"
                    )}&background=3b82f6&color=ffffff&size=32`
                  }
                  alt={endorserInfo.name}
                  className="edt-endorser-avatar"
                  title={`${endorserInfo.name}${
                    endorser.endorsementLevel
                      ? ` (${endorser.endorsementLevel})`
                      : ""
                  }`}
                />
              );
            })}
            {endorsers.length > 5 && (
              <span className="edt-endorsers-more">
                +{endorsers.length - 5}
              </span>
            )}
          </div>
          {endorsers.length > 0 && (
            <button
              className="edt-expand-btn"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Hide" : "Show all"}
              {expanded ? <FiChevronUp /> : <FiChevronDown />}
            </button>
          )}
        </div>
      )}

      {/* Expanded Endorsers List - with backend format support */}
      <AnimatePresence>
        {expanded && endorsers.length > 0 && (
          <motion.div
            className="edt-endorsers-list"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            {endorsers.map((endorser, i) => {
              // Backend format: endorser object structure
              const endorserInfo = endorser.endorser || endorser;
              const endorsementLevel = endorser.endorsementLevel;
              const context = endorser.context;
              const message = endorser.message;
              const createdAt = endorser.createdAt || endorser.endorsedAt;

              return (
                <div key={i} className="edt-endorser-item">
                  <img
                    src={
                      endorserInfo.profilePhotoUrl ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        endorserInfo.name || "User"
                      )}&background=3b82f6&color=ffffff&size=40`
                    }
                    alt={endorserInfo.name}
                    className="edt-endorser-photo"
                  />
                  <div className="edt-endorser-info">
                    <span className="edt-endorser-name">
                      {endorserInfo.name || "Unknown"}
                      {endorserInfo.isVerified && (
                        <FiCheck className="edt-verified" />
                      )}
                    </span>
                    <span className="edt-endorser-role">
                      {endorserInfo.professionalRole ||
                        endorserInfo.field ||
                        "Researcher"}
                    </span>
                    {/* Endorsement Level */}
                    {endorsementLevel && (
                      <span
                        className={`edt-endorser-level ${endorsementLevel}`}
                        style={{
                          color:
                            endorsementLevelLabels[endorsementLevel]?.color,
                        }}
                      >
                        {endorsementLevelLabels[endorsementLevel]?.label ||
                          endorsementLevel}
                      </span>
                    )}
                    {/* Context */}
                    {context && (
                      <span className="edt-endorser-context">
                        via {context}
                      </span>
                    )}
                    {/* Message */}
                    {message && (
                      <p className="edt-endorser-message">"{message}"</p>
                    )}
                  </div>
                  {createdAt && (
                    <span className="edt-endorser-date">
                      {new Date(createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add Skill Modal
const AddSkillModal = ({ isOpen, onClose, onAdd }) => {
  const [skillName, setSkillName] = useState("");
  const [category, setCategory] = useState("technical");

  const categories = [
    { id: "technical", label: "Technical" },
    { id: "research", label: "Research" },
    { id: "leadership", label: "Leadership" },
    { id: "communication", label: "Communication" },
    { id: "creative", label: "Creative" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (skillName.trim()) {
      onAdd?.({ skill: skillName.trim(), category });
      setSkillName("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="edt-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="edt-modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="edt-modal-header">
          <h3>Add New Skill</h3>
          <button className="edt-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="edt-form-group">
            <label>Skill Name</label>
            <input
              type="text"
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              placeholder="e.g., Machine Learning"
              className="edt-form-input"
              autoFocus
            />
          </div>
          <div className="edt-form-group">
            <label>Category</label>
            <div className="edt-category-options">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`edt-category-btn ${
                    category === cat.id ? "active" : ""
                  }`}
                  onClick={() => setCategory(cat.id)}
                  style={{
                    "--cat-color": categoryColors[cat.id],
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="edt-modal-actions">
            <button type="button" className="edt-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="edt-btn-submit">
              <FiPlus />
              Add Skill
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Stats Overview
const StatsOverview = ({ skills }) => {
  const stats = useMemo(() => {
    const totalEndorsements = skills.reduce(
      (sum, s) => sum + (s.endorsements?.length || s.endorsementCount || 0),
      0
    );
    const topSkill = skills.reduce((top, s) => {
      const count = s.endorsements?.length || s.endorsementCount || 0;
      return count > (top?.count || 0) ? { ...s, count } : top;
    }, null);

    return [
      {
        label: "Total Skills",
        value: skills.length,
        icon: FiAward,
        color: "var(--prf-blue)",
      },
      {
        label: "Endorsements",
        value: totalEndorsements,
        icon: FiUsers,
        color: "var(--prf-green)",
      },
      {
        label: "Top Skill",
        value: topSkill?.skill || topSkill?.name || "-",
        icon: FiTrendingUp,
        color: "var(--prf-purple)",
        isText: true,
      },
    ];
  }, [skills]);

  return (
    <div className="edt-stats">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            className="edt-stat-card"
            variants={itemVariants}
            style={{ "--stat-color": stat.color }}
          >
            <div className="edt-stat-icon">
              <Icon />
            </div>
            <div className="edt-stat-info">
              <span className={`edt-stat-value ${stat.isText ? "text" : ""}`}>
                {stat.value}
              </span>
              <span className="edt-stat-label">{stat.label}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// Empty State
const EmptyState = ({ isOwnProfile, onAddSkill }) => (
  <motion.div className="edt-empty" variants={itemVariants}>
    <div className="edt-empty-icon">
      <FiAward />
    </div>
    <h3 className="edt-empty-title">No skills added yet</h3>
    <p className="edt-empty-text">
      {isOwnProfile
        ? "Add skills to showcase your expertise and get endorsed by others"
        : "This user hasn't added any skills yet"}
    </p>
    {isOwnProfile && (
      <motion.button
        className="edt-empty-btn"
        onClick={onAddSkill}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
      >
        <FiPlus />
        <span>Add Your First Skill</span>
      </motion.button>
    )}
  </motion.div>
);

// Main Component
const EndorsementsTab = ({
  skills = [],
  isOwnProfile,
  handleEndorse,
  handleAddSkill,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  // Filter skills based on search
  const filteredSkills = useMemo(() => {
    if (!searchQuery) return skills;
    const query = searchQuery.toLowerCase();
    return skills.filter(
      (s) =>
        (s.skill || s.name)?.toLowerCase().includes(query) ||
        s.category?.toLowerCase().includes(query)
    );
  }, [skills, searchQuery]);

  // Sort skills by endorsement count
  const sortedSkills = useMemo(() => {
    return [...filteredSkills].sort((a, b) => {
      const aCount = a.endorsements?.length || a.endorsementCount || 0;
      const bCount = b.endorsements?.length || b.endorsementCount || 0;
      return bCount - aCount;
    });
  }, [filteredSkills]);

  return (
    <motion.div
      className="edt-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Stats Overview */}
      {skills.length > 0 && <StatsOverview skills={skills} />}

      {/* Toolbar */}
      <motion.div className="edt-toolbar" variants={itemVariants}>
        <div className="edt-toolbar-title">
          <FiAward className="edt-toolbar-icon" />
          <h3>Skills & Endorsements</h3>
        </div>

        <div className="edt-toolbar-actions">
          {/* Search */}
          <div className="edt-search">
            <FiSearch className="edt-search-icon" />
            <input
              type="text"
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="edt-search-input"
            />
          </div>

          {/* Add Skill Button */}
          {isOwnProfile && (
            <motion.button
              className="edt-add-btn"
              onClick={() => setShowAddModal(true)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiPlus />
              <span>Add Skill</span>
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Skills Grid */}
      <div className="edt-skills-grid">
        {sortedSkills.length === 0 ? (
          <EmptyState
            isOwnProfile={isOwnProfile}
            onAddSkill={() => setShowAddModal(true)}
          />
        ) : (
          sortedSkills.map((skill, index) => (
            <SkillCard
              key={skill._id || index}
              skill={skill}
              isOwnProfile={isOwnProfile}
              onEndorse={handleEndorse}
            />
          ))
        )}
      </div>

      {/* Add Skill Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddSkillModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onAdd={(newSkill) => {
              handleAddSkill?.(newSkill);
              setShowAddModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EndorsementsTab;
